// ============================================================
// shared/http.js — le contrat HTTP entre le front et l'API.
// ------------------------------------------------------------
// Une seule déclaration des en-têtes personnalisés, importée des DEUX
// côtés. C'est le même principe que `shared/pricing.js` : ce qui doit
// coïncider entre le navigateur et le serveur vit à un seul endroit,
// sinon il finit par diverger.
//
// ── Pourquoi ce fichier existe ──
//
// Le 3 août 2026, le dépôt d'un plan répondait « Serveur injoignable »
// sans qu'aucune requête n'atteigne l'API. Cause : le front envoyait
// `X-Filename`, en-tête absent de la liste `allowedHeaders` du CORS. Un
// en-tête personnalisé rend la requête « non simple » ; le navigateur
// envoie donc un préflight `OPTIONS`, l'API répond
// `Access-Control-Allow-Headers: Content-Type,X-CSRF-Token`, et le
// navigateur **bloque la vraie requête**. Elle ne part jamais. Aucune
// trace côté serveur, aucun code HTTP côté client.
//
// Le piège du diagnostic : le préflight lui-même répondait **204**.
// Regarder son statut ne discrimine pas — c'est son CONTENU qui était
// faux. Encore un contrôle qui répond sans trancher (CLAUDE.md §11,
// règle 3bis).
//
// C'est le TROISIÈME défaut de la famille « deux origines », après le
// 405 du 31 juillet et le blocage des écritures du 3 août. Sous une
// seule origine il n'y a pas de préflight, et aucun des trois n'aurait
// pu se produire (CLAUDE.md §10bis).
//
// La règle : **tout en-tête que le front envoie est déclaré ici**, et
// un test vérifie que le CORS les autorise tous. Ajouter un en-tête
// sans passer par ce fichier casse le déploiement en ligne sans rien
// casser en développement.
// ============================================================

/** Jeton anti-CSRF, sur toute écriture. */
export const CSRF_HEADER = 'X-CSRF-Token';

/** Nom d'origine d'un fichier déposé, encodé en URI. Affichage seul. */
export const FILENAME_HEADER = 'X-Filename';

/**
 * TOUS les en-têtes personnalisés que le navigateur peut envoyer.
 *
 * `Content-Type` et `Accept` n'y figurent pas : ils font partie des
 * en-têtes « simples » au sens CORS, mais `Content-Type` doit tout de
 * même être autorisé dès qu'il vaut autre chose que les trois valeurs
 * simples — d'où sa présence dans `ALLOWED_REQUEST_HEADERS`.
 */
export const CLIENT_CUSTOM_HEADERS = [CSRF_HEADER, FILENAME_HEADER];

/** La liste exacte à donner au middleware CORS. */
export const ALLOWED_REQUEST_HEADERS = ['Content-Type', 'Accept', ...CLIENT_CUSTOM_HEADERS];

/**
 * Les en-têtes de RÉPONSE que le navigateur a le droit de lire.
 *
 * ── Pourquoi il en faut une liste — 25 août 2026 ──
 *
 * Par défaut, une réponse inter-origines n'expose que six en-têtes, et
 * `Content-Disposition` n'en fait pas partie. Le serveur envoyait donc
 * `filename="2026-08-063.pdf"` depuis toujours, et le front ne pouvait
 * pas le lire : il récupérait le PDF en blob, et un blob n'a pas de nom.
 * Charles enregistrait son devis sous un identifiant.
 *
 * C'est la même famille que `X-Filename` du 3 août : un en-tête qui
 * n'est pas déclaré n'existe pas pour le navigateur, et rien ne le dit.
 * La différence est qu'ici la requête aboutit — seule l'information est
 * silencieusement retirée, ce qui est plus difficile à voir.
 *
 * C'est aussi une des six lignes que la bascule vers une seule origine
 * ferait disparaître (§10bis) : en *same-origin*, tous les en-têtes sont
 * lisibles sans rien déclarer.
 */
export const EXPOSED_RESPONSE_HEADERS = ['Content-Disposition'];
